import _ from 'lodash';
import { MapDataCountry } from './MapDataCountry';

export enum RangeType {
  First = "first",
  Middle = "middle",
  Last = "last",
  NoData = "noData"
}

export class MapDataRange {
  static palette: string[] = ['rgb(235, 235, 235)', "rgb(208, 226, 210)", "rgb(161, 197, 165)", "rgb(115, 169, 120)", "rgb(68, 140, 75)", "rgb(22, 112, 31)"];
  static fromData(data: Map<string, MapDataCountry>) {
    const sortedCountries = this.sortData([...data.values()]);
    if (sortedCountries.length === 0) {
      return [];
    }
    const [noDataColor, ...palette] = this.palette;
    const countriesWithData = sortedCountries.filter(e => e.value !== null && e.value !== undefined);
    const values = _.uniq(countriesWithData.map(e => e.roundedValue)) as number[];
    const rangeLength = Math.ceil(values.length / palette.length);
    const noDataRange = new MapDataRange(noDataColor, [], RangeType.NoData);
    const ranges: MapDataRange[] = _.range(0, palette.length).map(index => {
      const data = values.slice(index * rangeLength, (index + 1) * rangeLength + 1);
      const color = palette[index];
      const isLast = index === palette.length - 1 || data.length < (rangeLength + 1);
      const isFirst = index === 0;
      const rangeType = isFirst ? RangeType.First : isLast ? RangeType.Last : RangeType.Middle;
      return new MapDataRange(color, data, rangeType);
    }).concat(noDataRange);

    if (window.location.hostname === 'localhost') {
      sortedCountries.forEach(country => {
        const countryValue = country.roundedValue;
        const rangeIndex = ranges.findIndex(r => r.check(countryValue));
        const [range, nextRange, prevRange] = [0, +1, -1].map(i => ranges[rangeIndex + i]);

        let hasError = false;
        switch (range?.type) {
          case RangeType.First:
            hasError = !(countryValue < this.displayCastInt(nextRange ? nextRange.displayFrom : range.displayTo));
            break;
          case RangeType.Middle:
            hasError = !(countryValue >= this.displayCastInt(nextRange.displayFrom));
            hasError = !(countryValue <= this.displayCastInt((nextRange && nextRange.type !== RangeType.Last) ? nextRange.displayFrom : range.displayTo));
            break;
          case RangeType.Last:
            hasError = !(countryValue > this.displayCastInt(prevRange ? prevRange.displayTo : range.displayFrom));
            break;
          default:
            break;
        }

        if (hasError) {
          console.warn(`Legend range not valide for country ${country.name}. Value ${countryValue}`);
        }
      });
    }
    return ranges;
  }

  static sortData = (data: MapDataCountry[]) => _.orderBy(_.orderBy(data, e => e.code), e => e.value);
  static sortValues = (data: number[]) => _.orderBy(data, e => e);

  from?: number;
  to?: number;

  get displayFrom() {
    return this.display(this.from);
  }

  get displayTo() {
    return this.display(this.to);
  }

  data: number[];

  constructor(public color: string, data: number[], public type: RangeType) {
    this.data = MapDataRange.sortValues(data);
    this.from = this.data[0];
    this.to = this.data[this.data.length - 1];
  }

  check(value?: number) {
    if (this.type === RangeType.NoData) {
      return value === null || value === undefined;
    } else if (value === null || value === undefined || isNaN(value)) {
      return false;
    }

    const from = MapDataRange.displayCastInt(this.displayFrom);
    const to = MapDataRange.displayCastInt(this.displayTo);

    if (this.type === RangeType.First) {
      return value < to;
    }

    if (this.type === RangeType.Last) {
      return value > from;
    }

    return from <= value && value <= to;
  }

  private display(value?: number) {
    const symbolsLength = (value?.toFixed().length || 1) - 1;
    const formatter = Intl.NumberFormat('en-US', { maximumSignificantDigits: symbolsLength > 2 ? symbolsLength : 2 });
    return value !== null && value !== undefined ? formatter.format(value) : '';
  }

  private static displayCastInt = (displayValue: string) => Number(displayValue.replaceAll(',', ''));
}